import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { vendorReviewApi } from '../services/api';
import { VendorRatingSummary } from '../types';
import { extractApiError } from '../utils/errorUtils';

/**
 * VendorRatingBadge Component (Issue #956)
 *
 * Compact rating display for vendor cards and profiles.
 *
 * Features:
 * - Star rating display (filled/half/empty stars)
 * - Average rating number (e.g., "4.7")
 * - Total review count (e.g., "(42 reviews)")
 * - Click to scroll to reviews section
 * - Color-coded by rating level
 */

interface VendorRatingBadgeProps {
  vendorId: number;
  averageRating?: number;
  totalReviews?: number;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

const VendorRatingBadge: React.FC<VendorRatingBadgeProps> = ({
  vendorId,
  averageRating: propsAverageRating,
  totalReviews: propsTotalReviews,
  size = 'md',
  onClick
}) => {
  const [ratingSummary, setRatingSummary] = useState<VendorRatingSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use props values if provided, otherwise fetch from API
  const averageRating = propsAverageRating ?? ratingSummary?.average_rating;
  const totalReviews = propsTotalReviews ?? ratingSummary?.total_reviews ?? 0;

  useEffect(() => {
    // Only fetch if we don't have props values
    if (propsAverageRating === undefined || propsTotalReviews === undefined) {
      fetchRatingSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, propsAverageRating, propsTotalReviews]);

  const fetchRatingSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await vendorReviewApi.getRatingSummary(vendorId);
      setRatingSummary(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to fetch rating summary:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating: number): string => {
    if (rating >= 4.5) return 'text-green-500';
    if (rating >= 3.5) return 'text-teal-500';
    if (rating >= 2.5) return 'text-yellow-500';
    if (rating >= 1.5) return 'text-orange-500';
    return 'text-red-500';
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const starSize = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
    const colorClass = getRatingColor(rating);

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={`full-${i}`} className={`${starSize} ${colorClass} fill-current`} />
      );
    }

    // Half star
    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative inline-block">
          <Star className={`${starSize} text-gray-300 fill-current`} />
          <div className="absolute top-0 left-0 overflow-hidden" style={{ width: '50%' }}>
            <Star className={`${starSize} ${colorClass} fill-current`} />
          </div>
        </div>
      );
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Star key={`empty-${i}`} className={`${starSize} text-gray-300 fill-current`} />
      );
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-1 text-gray-400" data-testid="vendor-rating-badge-loading">
        <Star className={size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} />
        <span className={size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'}>
          Loading...
        </span>
      </div>
    );
  }

  if (error) {
    return null; // Silently fail for badge component
  }

  if (totalReviews === 0 || averageRating === undefined) {
    return (
      <div className="flex items-center gap-1 text-gray-400" data-testid="vendor-rating-badge-no-reviews">
        <Star className={size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} />
        <span className={size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'}>
          No reviews yet
        </span>
      </div>
    );
  }

  const textSize = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';
  const containerClass = onClick
    ? 'flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity'
    : 'flex items-center gap-2';

  return (
    <div
      className={containerClass}
      onClick={onClick}
      data-testid="vendor-rating-badge"
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyPress={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="flex items-center gap-0.5" data-testid="star-rating">
        {renderStars(averageRating)}
      </div>
      <span className={`${textSize} font-semibold ${getRatingColor(averageRating)}`} data-testid="average-rating">
        {averageRating.toFixed(1)}
      </span>
      <span className={`${textSize} text-gray-500`} data-testid="review-count">
        ({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})
      </span>
    </div>
  );
};

export default VendorRatingBadge;
